// src/pages/admin/AdminPackagesPage.tsx
import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { FullScreenLoader } from "components/ui/Loader";
import { formatMoney } from "utils/money";
import { getErrorMessage, isVersionConflict, conflictCurrentVersion } from "utils/errors";
import {
  adminListPackages,
  adminCreatePackage,
  adminUpdatePackage,
  adminPublishPackage,
  adminArchivePackage,
  AdminPackage
} from "api/admin";

import PackageFormModal from "features/admin/components/PackageFormModal";

type LineGeom = {
  left: number;
  titleTop: number;
  titleBottom: number;
  containerTop: number;
  containerBottom: number;
};

const GOLD = "#f3dfae";

const AdminPackagesPage: React.FC = () => {
  const [items, setItems] = useState<AdminPackage[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string>("");

  const [showModal, setShowModal] =
    useState<false | { mode: "create" } | { mode: "edit"; pkg: AdminPackage }>(false);

  const pageRef = useRef<HTMLElement | null>(null);
  const titleRef = useRef<HTMLHeadingElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [geom, setGeom] = useState<LineGeom | null>(null);

  const load = async () => {
    setErr("");
    try {
      const res = await adminListPackages();
      setItems(res);
    } catch (e: any) {
      setErr(getErrorMessage(e, "Failed to load packages"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useLayoutEffect(() => {
    const page = pageRef.current;
    const title = titleRef.current;
    const container = containerRef.current;
    if (!page || !title || !container) return;

    const compute = () => {
      const p = page.getBoundingClientRect();
      const t = title.getBoundingClientRect();
      const c = container.getBoundingClientRect();

      const left = Math.round(t.left + t.width / 2 - p.left);

      setGeom({
        left: Math.max(0, left),
        titleTop: Math.max(0, Math.round(t.top - p.top)),
        titleBottom: Math.max(0, Math.round(t.bottom - p.top)),
        containerTop: Math.max(0, Math.round(c.top - p.top)),
        containerBottom: Math.max(0, Math.round(c.bottom - p.top))
      });
    };

    compute();

    const ro = new ResizeObserver(() => compute());
    ro.observe(page);
    ro.observe(title);
    ro.observe(container);

    window.addEventListener("resize", compute);

    return () => {
      ro.disconnect();
      window.removeEventListener("resize", compute);
    };
  }, [items.length, err]);

  const onCreate = async (values: {
    name: string;
    slug?: string;
    short_description: string;
    long_description?: string | null;
    price_cents: number;
    currency: string;
  }) => {
    try {
      const created = await adminCreatePackage({
        name: values.name,
        slug: values.slug!,
        short_description: values.short_description,
        long_description: values.long_description,
        price_cents: values.price_cents,
        currency: values.currency
      });
      setItems((prev) => [created, ...prev]);
    } catch (e: any) {
      setErr(getErrorMessage(e, "Failed to create package"));
    }
  };

  const onEdit = async (
    pkg: AdminPackage,
    values: {
      name: string;
      short_description: string;
      long_description?: string | null;
      price_cents: number;
      currency: string;
      expected_lock_version?: number;
    }
  ) => {
    try {
      const updated = await adminUpdatePackage(pkg.id, {
        name: values.name,
        short_description: values.short_description,
        long_description: values.long_description,
        price_cents: values.price_cents,
        currency: values.currency,
        expected_lock_version: values.expected_lock_version ?? pkg.lock_version
      });
      setItems((prev) => prev.map((x) => (x.id === updated.id ? updated : x)));
    } catch (e: any) {
      if (isVersionConflict(e)) {
        const current = conflictCurrentVersion(e);
        throw new Error(
          current
            ? `Version conflict (current v${current}). Please reload and try again.`
            : "Version conflict. Please reload."
        );
      }
      throw e;
    }
  };

  const quickStatus = async (p: AdminPackage, next: "published" | "archived") => {
    setErr("");
    try {
      const updated =
        next === "published"
          ? await adminPublishPackage(p.id, p.lock_version)
          : await adminArchivePackage(p.id, p.lock_version);
      setItems((prev) => prev.map((x) => (x.id === updated.id ? updated : x)));
    } catch (e: any) {
      if (isVersionConflict(e)) {
        const current = conflictCurrentVersion(e);
        setErr(
          current
            ? `Another change beat you (current v${current}). Refresh the list.`
            : "Version conflict. Please refresh."
        );
      } else {
        setErr(getErrorMessage(e, "Failed to change status"));
      }
    }
  };

  if (loading) return <FullScreenLoader label="Loading…" />;

  const TITLE_GAP_PX = 14;
  const CONTAINER_TOP_MARGIN_CLASS = "mt-16 sm:mt-20";

  return (
    <main ref={pageRef} className="relative min-h-screen px-4 py-12 sm:py-16">
      {/* Vertical gold line segments (with gaps for title + main container) */}
      {geom && (
        <>
          <div
            aria-hidden="true"
            className="pointer-events-none absolute z-0 w-px bg-[#f3dfae]/90"
            style={{
              left: geom.left,
              top: 0,
              bottom: `calc(100% - ${Math.max(0, geom.titleTop - TITLE_GAP_PX)}px)`
            }}
          />
          <div
            aria-hidden="true"
            className="pointer-events-none absolute z-0 w-px bg-[#f3dfae]/90"
            style={{
              left: geom.left,
              top: geom.titleBottom + TITLE_GAP_PX,
              bottom: `calc(100% - ${geom.containerTop}px)`
            }}
          />
          <div
            aria-hidden="true"
            className="pointer-events-none absolute z-0 w-px bg-[#f3dfae]/90"
            style={{
              left: geom.left,
              top: geom.containerBottom,
              bottom: 0
            }}
          />
        </>
      )}

      <div className="relative z-10">
        {/* Title */}
        <header className="text-center">
          <h1
            ref={titleRef}
            className="font-display text-3xl sm:text-4xl font-semibold tracking-tight text-[#ceddf2]"
          >
            Admin · Packages
          </h1>
        </header>

        {/* Main container */}
        <section className={[CONTAINER_TOP_MARGIN_CLASS, "flex justify-center"].join(" ")}>
          <div
            ref={containerRef}
            className="w-full md:w-3/5 bg-white rounded-2xl shadow-sm border border-slate-200 px-6 py-8 sm:px-8"
          >
            <div className="flex items-start justify-between gap-4">
              <Link
                to="/admin"
                className="font-ui text-xs uppercase tracking-wide text-slate-500 hover:opacity-80"
              >
                ← Back to Admin Home
              </Link>

              <button
                type="button"
                onClick={() => setShowModal({ mode: "create" })}
                className={[
                  "inline-flex items-center justify-center rounded-full",
                  "bg-[#f3dfae] px-5 py-2",
                  "text-sm tracking-[0.18em] uppercase text-[#151A3A]",
                  "shadow hover:opacity-90 transition-opacity font-ui font-bold"
                ].join(" ")}
              >
                + Add Package
              </button>
            </div>

            {err && (
              <div className="mt-6 border border-red-300 bg-red-50 text-red-700 p-3 rounded">
                {err}
              </div>
            )}

            <div className="mt-8">
              <h2 className="font-ui font-bold uppercase tracking-wide text-slate-800">
                All Packages
              </h2>

              {items.length === 0 ? (
                <p className="mt-3 font-body text-slate-600">No packages yet.</p>
              ) : (
                <div className="mt-5 space-y-4">
                  {items.map((p) => (
                    <div
                      key={p.id}
                      className={[
                        "rounded-2xl border p-5",
                        "border-slate-200 bg-white",
                        "shadow-sm"
                      ].join(" ")}
                    >
                      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                        <div className="min-w-0">
                          <div className="font-display text-xl font-semibold text-slate-900 truncate">
                            {p.name}
                          </div>
                          <div className="mt-1 font-body text-sm text-slate-700">
                            {p.short_description}
                          </div>

                          <div className="mt-3 flex items-center gap-3">
                            <div className="font-body text-lg text-slate-900">
                              {formatMoney(p.price_cents, p.currency)}
                            </div>
                            <span
                              className={[
                                "inline-flex items-center rounded-full px-3 py-1",
                                "font-ui text-xs uppercase tracking-wide",
                                p.status === "published"
                                  ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
                                  : p.status === "archived"
                                  ? "bg-slate-100 text-slate-700 border border-slate-200"
                                  : "bg-amber-50 text-amber-800 border border-amber-200"
                              ].join(" ")}
                            >
                              {p.status}
                            </span>
                          </div>

                          <div className="mt-2 font-ui text-xs uppercase tracking-wide text-slate-500">
                            id:{p.id} · v{p.lock_version}
                          </div>
                        </div>

                        <div className="flex flex-wrap gap-2">
                          <button
                            type="button"
                            onClick={() => setShowModal({ mode: "edit", pkg: p })}
                            className={[
                              "inline-flex items-center justify-center rounded-full",
                              "px-4 py-2",
                              "border border-slate-200 bg-white",
                              "text-xs tracking-[0.18em] uppercase text-[#151A3A]",
                              "hover:bg-slate-50 transition-colors font-ui font-bold"
                            ].join(" ")}
                          >
                            Edit
                          </button>

                          {p.status !== "published" && (
                            <button
                              type="button"
                              onClick={() => quickStatus(p, "published")}
                              className={[
                                "inline-flex items-center justify-center rounded-full",
                                "px-4 py-2",
                                "bg-[#f3dfae]",
                                "text-xs tracking-[0.18em] uppercase text-[#151A3A]",
                                "hover:opacity-90 transition-opacity font-ui font-bold"
                              ].join(" ")}
                            >
                              Publish
                            </button>
                          )}

                          {p.status !== "archived" && (
                            <button
                              type="button"
                              onClick={() => quickStatus(p, "archived")}
                              className={[
                                "inline-flex items-center justify-center rounded-full",
                                "px-4 py-2",
                                "border",
                                "text-xs tracking-[0.18em] uppercase font-ui font-bold",
                                "hover:opacity-90 transition-opacity"
                              ].join(" ")}
                              style={{ borderColor: GOLD, color: "#151A3A" }}
                            >
                              Archive
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </section>

        {showModal && showModal.mode === "create" && (
          <PackageFormModal mode="create" onSubmit={onCreate} onClose={() => setShowModal(false)} />
        )}
        {showModal && showModal.mode === "edit" && (
          <PackageFormModal
            mode="edit"
            initial={showModal.pkg}
            onSubmit={(v) => onEdit(showModal.pkg, v)}
            onClose={() => setShowModal(false)}
          />
        )}
      </div>
    </main>
  );
};

export default AdminPackagesPage;
